import type { Edge, Node } from '@xyflow/react';
import { useEffect, useMemo, useRef } from 'react';
import type { BaseNodeData } from '../../../../web/containers/AutomationFlows/types';
import { getActiveFlowContacts } from '../../../../web/containers/AutomationFlows/utils/contactConversion';
import {
	type ContactStepStatusLike,
	enrichEdgesWithSimulatedContacts,
	enrichNodesWithSimulatedContacts,
} from '../../../../web/containers/AutomationFlows/utils/simulateContactFlow';
import type { TestContact } from '../testing/types';

interface UseCanvasSyncProps {
	/** Expanded nodes from the API. */
	expandedNodes: Node<BaseNodeData>[];
	/** Expanded edges from the API. */
	expandedEdges: Edge[];
	/** Whether the flow expansion is in progress. */
	isExpanding: boolean;
	/** Which version the expanded data represents ('draft' | 'published'). */
	expandedVersion: string | undefined;
	/** Whether the canvas is viewing draft or published. */
	useDraft: boolean;
	/** Whether the canvas has been initialized at least once. */
	isInitialized: boolean;
	/** Current test contacts for simulation overlay. */
	testContacts: TestContact[];
	/** Ref to current nodes (for enrichment and structural change detection). */
	nodesRef: React.MutableRefObject<Node<BaseNodeData>[]>;
	/** State setters. */
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	setCanvasSyncVersion: React.Dispatch<React.SetStateAction<number>>;
	setIsInitialized: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * useCanvasSync - Handles syncing expanded nodes/edges from the API into
 * local canvas state.
 *
 * Encapsulates three effects:
 * 1. Version-based sync (draft ↔ published toggle)
 * 2. Contact enrichment (overlay simulated contacts)
 * 3. Structural change detection (new/removed nodes after refetch)
 */
export function useCanvasSync({
	expandedNodes,
	expandedEdges,
	isExpanding,
	expandedVersion,
	useDraft,
	isInitialized,
	testContacts,
	nodesRef,
	setNodes,
	setEdges,
	setCanvasSyncVersion,
	setIsInitialized,
}: UseCanvasSyncProps) {
	// -----------------------------------------------------------------------
	// Effect 1: Sync expanded nodes/edges into local state on version change
	// -----------------------------------------------------------------------
	const prevSyncedVersionRef = useRef<string | null>(null);

	useEffect(() => {
		const requestedVersion = useDraft ? 'draft' : 'published';
		const versionMatches = expandedVersion === requestedVersion;
		const needsSync = versionMatches && prevSyncedVersionRef.current !== requestedVersion;

		if (needsSync && expandedNodes.length > 0 && !isExpanding) {
			setNodes(expandedNodes);
			setEdges(expandedEdges);
			const isVersionSwitch = prevSyncedVersionRef.current !== null;
			if (isVersionSwitch) {
				setCanvasSyncVersion(prev => prev + 1);
			}
			prevSyncedVersionRef.current = requestedVersion;
			if (!isInitialized) {
				setIsInitialized(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expandedNodes, expandedEdges, isExpanding, expandedVersion, useDraft]);

	// -----------------------------------------------------------------------
	// Effect 2: Contact enrichment
	// -----------------------------------------------------------------------
	const activeContacts = useMemo(() => getActiveFlowContacts(testContacts), [testContacts]);

	const contactStepsByContactId = useMemo(() => {
		const map = new Map<string, ContactStepStatusLike[] | undefined>();
		for (const testContact of testContacts) {
			if (!testContact.isSelected || !testContact.contact.id) {
				continue;
			}
			map.set(testContact.contact.id, testContact.steps);
		}
		return map;
	}, [testContacts]);

	useEffect(() => {
		setNodes(currentNodes => enrichNodesWithSimulatedContacts(currentNodes, activeContacts, contactStepsByContactId));
		setEdges(currentEdges =>
			enrichEdgesWithSimulatedContacts(nodesRef.current, currentEdges, activeContacts, contactStepsByContactId)
		);
	}, [activeContacts, contactStepsByContactId, setNodes, setEdges, nodesRef]);

	// -----------------------------------------------------------------------
	// Effect 3: Structural change detection (new nodes after refetch)
	// -----------------------------------------------------------------------
	const prevExpandedNodeIdsRef = useRef<string>('');

	useEffect(() => {
		if (!isInitialized || isExpanding || expandedNodes.length === 0) {
			return;
		}
		const expandedIds = expandedNodes
			.map(n => n.id)
			.sort()
			.join(',');
		if (expandedIds === prevExpandedNodeIdsRef.current) {
			return;
		}
		const prevIds = new Set(prevExpandedNodeIdsRef.current.split(',').filter(Boolean));
		const currentIds = new Set(expandedNodes.map(n => n.id));
		const hasNewNodes = prevExpandedNodeIdsRef.current !== '' && [...currentIds].some(id => !prevIds.has(id));
		prevExpandedNodeIdsRef.current = expandedIds;

		if (hasNewNodes) {
			// Merge new nodes while preserving existing canvas state that React Flow
			// manages internally (measured dimensions) and user-driven state
			// (automationStep edits, isExpanded).
			// Without preserving `measured`, React Flow re-reports dimensions for
			// every node, creating an infinite onNodesChange → re-render loop.
			const currentNodeMap = new Map(nodesRef.current.map(n => [n.id, n]));
			const mergedNodes = expandedNodes.map(newNode => {
				const existingNode = currentNodeMap.get(newNode.id);
				if (existingNode) {
					const preserved: Record<string, unknown> = {};
					if ('automationStep' in existingNode.data && existingNode.data.automationStep) {
						preserved.automationStep = (existingNode.data as unknown as { automationStep: unknown }).automationStep;
					}
					if ('isExpanded' in existingNode.data) {
						preserved.isExpanded = (existingNode.data as unknown as { isExpanded: boolean }).isExpanded;
					}
					return {
						...newNode,
						// Preserve the canvas's current position so that server-expansion
						// positions don't override the layout engine's (or optimistic update's)
						// computed positions. The layout engine will reposition if needed when
						// new-node dimension changes trigger the next Dagre pass.
						position: existingNode.position,
						// Preserve React Flow's measured dimensions to prevent
						// re-measurement loops after structural syncs.
						measured: existingNode.measured,
						data: {
							...newNode.data,
							...preserved,
						},
					};
				}
				// New node not in current canvas: keep server-provided expansion state
				// to avoid expansion/collapse flicker during structural sync.
				return newNode;
			});
			setNodes(mergedNodes);
			setEdges(expandedEdges);
		}
	}, [expandedNodes, expandedEdges, isInitialized, isExpanding, setNodes, setEdges, nodesRef]);

	return { activeContacts };
}
